import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { useDrawer } from 'components/app/DrawerContext';
import React, { useEffect, useRef, useState } from 'react';
import {
  FaCog,
  FaFileSignature,
  FaFont,
  FaPencilAlt,
  FaSave,
  FaTimesCircle,
  FaTrash,
  FaUpload
} from 'react-icons/fa';
import { MdBusiness, MdDateRange } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import SignatureCanvas from 'react-signature-canvas';
import {
  clearError,
  manageOperationsPercentage,
  manageTrustConfiguration,
  selectAllConfigs,
  selectError,
  selectLoading,
  selectTrustConfig,
  type OperationsConfig
} from 'slices/fundManagementInvestment/fundManagementConfigurationSlice';

interface TrustConfig {
  trustName: string;
  trustFundManagerSignatureBase64: string | null;
  trustDeedExecutionDate: string;
  investorEmail: string;
  investorSignatureBase64: string | null;
}

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (signature: string) => void;
  title: string;
}

const SignatureModal: React.FC<SignatureModalProps> = ({ open, onClose, onSave, title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const signaturePadRef = useRef<SignatureCanvas>(null);

  const [tabValue, setTabValue] = useState(0);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [typedName, setTypedName] = useState('');
  const [selectedFont, setSelectedFont] = useState<'cursive' | 'serif' | 'monospace'>('cursive');

  const handleClear = () => {
    if (tabValue === 0 && signaturePadRef.current) {
      signaturePadRef.current.clear();
    } else if (tabValue === 1) {
      setUploadedImage(null);
    } else {
      setTypedName('');
    }
  };

  const handleSave = () => {
    let signatureData: string | null = null;

    if (tabValue === 0) {
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        signatureData = signaturePadRef.current.getTrimmedCanvas().toDataURL('image/png');
      }
    } else if (tabValue === 1) {
      signatureData = uploadedImage;
    } else if (tabValue === 2) {
      if (typedName.trim()) {
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 100;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = '#000000';
          ctx.font = `40px ${selectedFont}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
          signatureData = canvas.toDataURL('image/png');
        }
      }
    }

    if (signatureData) {
      onSave(signatureData);
      onClose();
      handleClear();
    } else {
      alert('Please create a signature before saving');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('File size should not exceed 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const fontStyles = {
    cursive: { fontFamily: 'Brush Script MT, cursive', label: 'Script' },
    serif: { fontFamily: 'Georgia, serif', label: 'Formal' },
    monospace: { fontFamily: 'Courier New, monospace', label: 'Modern' }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: { xs: 1.5, md: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', md: '1.1rem' } }}
          >
            {title}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <FaTimesCircle size={isMobile ? 16 : 20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: { xs: 1, md: 2 }, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<FaPencilAlt size={isMobile ? 12 : 14} />}
            label={
              <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>Draw</Typography>
            }
            iconPosition="start"
            sx={{ textTransform: 'none', minHeight: { xs: 40, md: 48 }, px: { xs: 1, md: 2 } }}
          />
          <Tab
            icon={<FaUpload size={isMobile ? 12 : 14} />}
            label={
              <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>Upload</Typography>
            }
            iconPosition="start"
            sx={{ textTransform: 'none', minHeight: { xs: 40, md: 48 }, px: { xs: 1, md: 2 } }}
          />
          <Tab
            icon={<FaFont size={isMobile ? 12 : 14} />}
            label={
              <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>Type</Typography>
            }
            iconPosition="start"
            sx={{ textTransform: 'none', minHeight: { xs: 40, md: 48 }, px: { xs: 1, md: 2 } }}
          />
        </Tabs>

        <Box sx={{ minHeight: { xs: 200, md: 300 } }}>
          {tabValue === 0 && (
            <Box>
              <Paper
                elevation={2}
                sx={{
                  border: `2px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: 'hidden'
                }}
              >
                <SignatureCanvas
                  ref={signaturePadRef}
                  canvasProps={{
                    width: isMobile ? 280 : 600,
                    height: isMobile ? 150 : 200,
                    className: 'signature-canvas',
                    style: { width: '100%', height: isMobile ? '150px' : '200px' }
                  }}
                  backgroundColor="#ffffff"
                  penColor="#000000"
                />
              </Paper>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block', fontSize: { xs: '0.65rem', md: '0.75rem' } }}
              >
                Draw your signature using mouse or touch
              </Typography>
            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="upload-signature"
                type="file"
                onChange={handleFileUpload}
              />
              {uploadedImage ? (
                <Paper
                  elevation={2}
                  sx={{
                    p: { xs: 1, md: 2 },
                    borderRadius: 1,
                    border: `2px solid ${theme.palette.primary.main}`
                  }}
                >
                  <Box
                    component="img"
                    src={uploadedImage}
                    alt="Uploaded signature"
                    sx={{
                      width: '100%',
                      height: { xs: 120, md: 200 },
                      objectFit: 'contain',
                      borderRadius: 1
                    }}
                  />
                </Paper>
              ) : (
                <label htmlFor="upload-signature">
                  <Paper
                    elevation={1}
                    sx={{
                      p: { xs: 2, md: 4 },
                      borderRadius: 1,
                      border: `2px dashed ${theme.palette.divider}`,
                      textAlign: 'center',
                      cursor: 'pointer',
                      minHeight: { xs: 120, md: 200 },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                  >
                    <FaUpload size={isMobile ? 28 : 48} color={theme.palette.text.secondary} />
                    <Typography
                      variant="h6"
                      sx={{
                        mt: { xs: 1, md: 2 },
                        fontWeight: 600,
                        fontSize: { xs: '0.8rem', md: '1rem' }
                      }}
                    >
                      Click to upload
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, color: '#666', fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                    >
                      PNG, JPG (max 2MB)
                    </Typography>
                  </Paper>
                </label>
              )}
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <TextField
                fullWidth
                label="Type your name"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Enter your full name"
                size={isMobile ? 'small' : 'medium'}
                sx={{ mb: { xs: 1, md: 2 } }}
                InputLabelProps={{ sx: { fontSize: { xs: '0.75rem', md: '0.875rem' } } }}
                InputProps={{ sx: { fontSize: { xs: '0.75rem', md: '0.875rem' } } }}
              />

              <Typography
                variant="subtitle2"
                sx={{ mb: 0.5, fontWeight: 600, fontSize: { xs: '0.7rem', md: '0.8rem' } }}
              >
                Select Font Style:
              </Typography>
              <Grid container spacing={{ xs: 1, md: 2 }} sx={{ mb: { xs: 1.5, md: 3 } }}>
                {Object.entries(fontStyles).map(([key, style]) => (
                  <Grid item xs={4} key={key}>
                    <Button
                      fullWidth
                      variant={selectedFont === key ? 'contained' : 'outlined'}
                      onClick={() => setSelectedFont(key as any)}
                      sx={{
                        textTransform: 'none',
                        py: { xs: 0.5, md: 1 },
                        fontSize: { xs: '0.65rem', md: '0.75rem' }
                      }}
                    >
                      {style.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>

              {typedName && (
                <Paper
                  elevation={2}
                  sx={{
                    p: { xs: 1.5, md: 3 },
                    borderRadius: 1,
                    border: `2px solid ${theme.palette.primary.main}`,
                    minHeight: { xs: 80, md: 150 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#ffffff'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '1.5rem', md: '2.5rem' },
                      fontFamily: fontStyles[selectedFont].fontFamily,
                      color: '#000000'
                    }}
                  >
                    {typedName}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 1, md: 3 }, pb: { xs: 1, md: 2 } }}>
        <Button
          variant="outlined"
          onClick={handleClear}
          startIcon={<FaTrash size={isMobile ? 10 : 12} />}
          sx={{
            textTransform: 'none',
            fontSize: { xs: '0.7rem', md: '0.8rem' },
            py: { xs: 0.5, md: 0.75 }
          }}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<FaSave size={isMobile ? 10 : 12} />}
          sx={{
            textTransform: 'none',
            fontSize: { xs: '0.7rem', md: '0.8rem' },
            py: { xs: 0.5, md: 0.75 }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FundManagementConfigurationPanel: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { fundManagementAdminRight } = useDrawer();

  const [isAdmin] = useState(fundManagementAdminRight);

  const allConfigs = useSelector(selectAllConfigs);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const trustConfigFromStore = useSelector(selectTrustConfig);
  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);


  console.log('trustConfigFromStore', trustConfigFromStore);
  const getCurrentUserEmail = (): string => {
    const loginInfo = localStorage.getItem('user-details');
    if (loginInfo) {
      try {
        const userDetails = JSON.parse(loginInfo);
        return userDetails?.email || '';
      } catch (error) {
        return '';
      }
    }
    return '';
  };

  const userEmail = getCurrentUserEmail();

  const [values, setValues] = useState({
    buyUnitsIncome: '',
    buyUnitsGrowth: '',
    sellUnitsIncome: '',
    sellUnitsGrowth: ''
  });

  const [trustInfo, setTrustInfo] = useState<TrustConfig>({
    trustName: '',
    trustFundManagerSignatureBase64: null,
    trustDeedExecutionDate: '',
    investorEmail: userEmail,
    investorSignatureBase64: null
  });

  console.log('trustInfo state:', trustInfo);

  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [currentSignatureType, setCurrentSignatureType] = useState<'investor' | 'admin'>(
    'investor'
  );
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  const ensureDataUri = (base64: string | null | undefined): string | null => {
    if (!base64) return null;
    return base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;
  };

  const removeDataUri = (base64: string | null): string | null => {
    if (!base64) return null;
    return base64.replace(/^data:image\/[a-z]+;base64,/, '');
  };

  useEffect(() => {
    if (selectedProjectId) {
      const configs = [
        { unitType: 'incomeUnits' as const, perType: 'buy' as const },
        { unitType: 'growthUnits' as const, perType: 'buy' as const },
        { unitType: 'incomeUnits' as const, perType: 'sell' as const },
        { unitType: 'growthUnits' as const, perType: 'sell' as const }
      ];

      configs.forEach((config) => {
        dispatch(
          manageOperationsPercentage({
            action: 'get',
            groupId: selectedProjectId,
            serviceId: 'fundManagementInvestment',
            unitType: config.unitType,
            perType: config.perType
          }) as any
        );
      });

      dispatch(
        manageTrustConfiguration({
          action: 'get',
          groupId: selectedProjectId,
          serviceId: 'fundManagementInvestment',
          unitType: 'growthUnits',
          isAdmin,
          investorEmail: userEmail
        }) as any
      );
    }
  }, [selectedProjectId, dispatch, isAdmin, userEmail]);

  useEffect(() => {
    if (allConfigs && allConfigs.length > 0) {
      const buyIncome = allConfigs.find(
        (c: OperationsConfig) => c.unitType === 'incomeUnits' && c.perType === 'buy'
      );
      const buyGrowth = allConfigs.find(
        (c: OperationsConfig) => c.unitType === 'growthUnits' && c.perType === 'buy'
      );
      const sellIncome = allConfigs.find(
        (c: OperationsConfig) => c.unitType === 'incomeUnits' && c.perType === 'sell'
      );
      const sellGrowth = allConfigs.find(
        (c: OperationsConfig) => c.unitType === 'growthUnits' && c.perType === 'sell'
      );

      setValues({
        buyUnitsIncome: buyIncome?.percentage.toString() || '',
        buyUnitsGrowth: buyGrowth?.percentage.toString() || '',
        sellUnitsIncome: sellIncome?.percentage.toString() || '',
        sellUnitsGrowth: sellGrowth?.percentage.toString() || ''
      });
    }
  }, [allConfigs]);

  useEffect(() => {
    if (isAdmin && trustConfigFromStore) {
      setTrustInfo({
        trustName: trustConfigFromStore.trustName || '',
        trustFundManagerSignatureBase64: ensureDataUri(
          trustConfigFromStore.trustFundManagerSignatureBase64
        ),
        trustDeedExecutionDate: trustConfigFromStore.trustDeedExecutionDate || '',
        investorEmail: trustConfigFromStore.investorEmail || userEmail,
        investorSignatureBase64: ensureDataUri(trustConfigFromStore.investorSignatureBase64)
      });
    } else if (!isAdmin && trustConfigFromStore) {
      setTrustInfo((prev) => ({
        ...prev,
        investorEmail: trustConfigFromStore.investorEmail || userEmail,
        investorSignatureBase64: ensureDataUri(trustConfigFromStore.investorSignatureBase64)
      }));
    }
  }, [trustConfigFromStore, trustConfigFromStore, isAdmin, userEmail]);

  const handleOpenSignatureModal = (type: 'investor' | 'admin') => {
    setCurrentSignatureType(type);
    setSignatureModalOpen(true);
  };

  const handleSaveSignature = (signature: string) => {
    if (currentSignatureType === 'investor') {
      setTrustInfo({ ...trustInfo, investorSignatureBase64: signature });
    } else {
      setTrustInfo({ ...trustInfo, trustFundManagerSignatureBase64: signature });
    }
    setSuccessMessage('Signature saved. Click "Save All Changes" to persist.');
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const handleRemoveSignature = (type: 'investor' | 'admin') => {
    if (type === 'investor') {
      setTrustInfo({ ...trustInfo, investorSignatureBase64: null });
    } else {
      setTrustInfo({ ...trustInfo, trustFundManagerSignatureBase64: null });
    }
  };

  const handleUpdate = (
    unitType: 'incomeUnits' | 'growthUnits',
    perType: 'buy' | 'sell',
    value: string
  ) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0 || num > 100) {
      alert('Please enter a valid percentage between 0-100');
      return;
    }

    dispatch(
      manageOperationsPercentage({
        action: 'update',
        groupId: selectedProjectId || '',
        serviceId: 'fundManagementInvestment',
        unitType,
        perType,
        percentage: num
      }) as any
    );

    const perTypeLabel = perType === 'buy' ? 'Buy' : 'Sell';
    const unitTypeLabel = unitType === 'incomeUnits' ? 'Income' : 'Growth';
    setSuccessMessage(`${perTypeLabel} Units - ${unitTypeLabel} updated!`);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const handleSaveAll = async () => {
    setSaveLoading(true);

    try {
      if (isAdmin) {
        const updates = [
          { unitType: 'incomeUnits' as const, perType: 'buy' as const, val: values.buyUnitsIncome },
          { unitType: 'growthUnits' as const, perType: 'buy' as const, val: values.buyUnitsGrowth },
          {
            unitType: 'incomeUnits' as const,
            perType: 'sell' as const,
            val: values.sellUnitsIncome
          },
          {
            unitType: 'growthUnits' as const,
            perType: 'sell' as const,
            val: values.sellUnitsGrowth
          }
        ];

        let validCount = 0;
        updates.forEach((u) => {
          const num = parseFloat(u.val);
          if (!isNaN(num) && num >= 0 && num <= 100) {
            dispatch(
              manageOperationsPercentage({
                action: 'update',
                groupId: selectedProjectId || '',
                serviceId: 'fundManagementInvestment',
                unitType: u.unitType,
                perType: u.perType,
                percentage: num
              }) as any
            );
            validCount++;
          }
        });

        await dispatch(
          manageTrustConfiguration({
            action: 'update',
            groupId: selectedProjectId || '',
            serviceId: 'fundManagementInvestment',
            unitType: 'growthUnits',
            isAdmin: true,
            investorEmail: trustInfo.investorEmail || userEmail,
            trustName: trustInfo.trustName,
            trustFundManagerSignatureBase64: removeDataUri(
              trustInfo.trustFundManagerSignatureBase64
            ),
            trustDeedExecutionDate: trustInfo.trustDeedExecutionDate,
            investorSignatureBase64: removeDataUri(trustInfo.investorSignatureBase64)
          }) as any
        ).unwrap();

        setSuccessMessage(`All saved! (${validCount} percentages updated)`);
      } else {
       

        await dispatch(
          manageTrustConfiguration({
            action: 'update',
            groupId: selectedProjectId || '',
            serviceId: 'fundManagementInvestment',
            unitType: 'growthUnits',
            isAdmin: false,
            investorEmail: userEmail,
            investorSignatureBase64: removeDataUri(trustInfo.investorSignatureBase64)
          }) as any
        ).unwrap();

        setSuccessMessage('Signature saved!');
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      alert('Failed to save');
    } finally {
      setSaveLoading(false);
    }
  };

  const SignatureDisplay = ({
    type,
    signature,
    onEdit,
    onRemove
  }: {
    type: string;
    signature: string | null;
    onEdit: () => void;
    onRemove: () => void;
  }) => (
    <Box>
      {signature ? (
        <Paper
          elevation={2}
          sx={{
            p: { xs: 1, md: 2 },
            borderRadius: { xs: 1, md: 1.5 },
            border: `2px solid ${theme.palette.primary.main}`
          }}
        >
          <Box
            component="img"
            src={signature}
            alt={`${type} signature`}
            sx={{
              width: '100%',
              height: { xs: 80, md: 120 },
              objectFit: 'contain',
              borderRadius: 1,
              backgroundColor: '#ffffff'
            }}
          />
          <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 }, mt: { xs: 0.5, md: 1 } }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FaPencilAlt size={isMobile ? 10 : 12} />}
              onClick={onEdit}
              fullWidth
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.65rem', md: '0.75rem' },
                py: { xs: 0.4, md: 0.5 }
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<FaTrash size={isMobile ? 10 : 12} />}
              onClick={onRemove}
              fullWidth
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.65rem', md: '0.75rem' },
                py: { xs: 0.4, md: 0.5 }
              }}
            >
              Remove
            </Button>
          </Box>
        </Paper>
      ) : (
        <Paper
          elevation={1}
          sx={{
            p: { xs: 1.5, md: 3 },
            borderRadius: { xs: 1, md: 1.5 },
            border: `2px dashed ${theme.palette.divider}`,
            textAlign: 'center',
            cursor: 'pointer',
            minHeight: { xs: 100, md: 150 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover
            }
          }}
          onClick={onEdit}
        >
          <FaFileSignature size={isMobile ? 24 : 32} color={theme.palette.text.secondary} />
          <Typography
            variant="body2"
            sx={{ mt: { xs: 0.5, md: 1 }, fontSize: { xs: '0.7rem', md: '0.8rem' }, color: '#666' }}
          >
            Click to add signature
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' }, color: '#999' }}
          >
            Draw, Upload, or Type
          </Typography>
        </Paper>
      )}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, md: 3 }, px: { xs: 0.5, md: 3 } }} component="div">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, md: 2 },
          mb: { xs: 1.5, md: 3 },
          px: { xs: 0.5, md: 0 }
        }}
      >
        <Box
          sx={{
            width: { xs: 32, md: 44 },
            height: { xs: 32, md: 44 },
            borderRadius: { xs: 1, md: 2 },
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FaCog size={isMobile ? 16 : 22} color="white" />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: '#333', fontSize: { xs: '0.85rem', md: '1.1rem' } }}
          >
            Fund Management Configuration
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#666', fontSize: { xs: '0.65rem', md: '0.85rem' } }}
          >
            {isAdmin
              ? 'Manage operational settings, trust information, and signatures'
              : 'Manage your signature'}
          </Typography>
        </Box>
      </Box>

      {success && (
        <Alert
          severity="success"
          sx={{
            mb: { xs: 1, md: 2 },
            borderRadius: { xs: 1, md: 2 },
            mx: { xs: 0.5, md: 0 },
            fontSize: { xs: '0.7rem', md: '0.875rem' }
          }}
          onClose={() => setSuccess(false)}
        >
          ✓ {successMessage}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: { xs: 1, md: 2 },
            borderRadius: { xs: 1, md: 2 },
            mx: { xs: 0.5, md: 0 },
            fontSize: { xs: '0.7rem', md: '0.875rem' }
          }}
          onClose={() => dispatch(clearError())}
        >
          ✗ {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ textAlign: 'center', py: { xs: 2, md: 4 } }}>
          <CircularProgress size={isMobile ? 24 : 32} />
          <Typography
            variant="body2"
            sx={{ mt: { xs: 1, md: 2 }, color: '#666', fontSize: { xs: '0.7rem', md: '0.85rem' } }}
          >
            Loading...
          </Typography>
        </Box>
      )}

      {!loading && (
        <>
          {isAdmin && (
            <Card
              sx={{
                mb: { xs: 1.5, md: 3 },
                borderRadius: { xs: 1, md: 2 },
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                mx: { xs: 0.5, md: 0 }
              }}
            >
              <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.5, md: 1 },
                    mb: { xs: 1, md: 2 }
                  }}
                >
                  <MdBusiness size={isMobile ? 14 : 20} color={theme.palette.primary.main} />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', md: '1rem' } }}
                  >
                    Trust Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: { xs: 1, md: 2 } }} />
                <Grid container spacing={{ xs: 1, md: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Trust Name"
                      value={trustInfo.trustName}
                      onChange={(e) => setTrustInfo({ ...trustInfo, trustName: e.target.value })}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdBusiness size={isMobile ? 14 : 18} />
                          </InputAdornment>
                        ),
                        sx: { fontSize: { xs: '0.7rem', md: '0.875rem' } }
                      }}
                      InputLabelProps={{ sx: { fontSize: { xs: '0.7rem', md: '0.875rem' } } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Deed Execution Date"
                      type="date"
                      value={trustInfo.trustDeedExecutionDate}
                      onChange={(e) =>
                        setTrustInfo({ ...trustInfo, trustDeedExecutionDate: e.target.value })
                      }
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                        sx: { fontSize: { xs: '0.7rem', md: '0.875rem' } }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdDateRange size={isMobile ? 14 : 18} />
                          </InputAdornment>
                        ),
                        sx: { fontSize: { xs: '0.7rem', md: '0.875rem' } }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          <Card
            sx={{
              mb: { xs: 1.5, md: 3 },
              borderRadius: { xs: 1, md: 2 },
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              mx: { xs: 0.5, md: 0 }
            }}
          >
            <CardContent sx={{ p: { xs: 1, md: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.5, md: 1 },
                  mb: { xs: 1, md: 2 }
                }}
              >
                <FaFileSignature size={isMobile ? 14 : 20} color={theme.palette.primary.main} />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', md: '1rem' } }}
                >
                  Signatures
                </Typography>
              </Box>
              <Divider sx={{ mb: { xs: 1, md: 2 } }} />
              <Grid container spacing={{ xs: 1.5, md: 3 }}>
                <Grid item xs={12} md={isAdmin ? 6 : 12}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      mb: { xs: 0.75, md: 1.5 },
                      fontSize: { xs: '0.7rem', md: '0.9rem' }
                    }}
                  >
                    Investor Signature
                  </Typography>
                  <SignatureDisplay
                    type="investor"
                    signature={trustInfo.investorSignatureBase64}
                    onEdit={() => handleOpenSignatureModal('investor')}
                    onRemove={() => handleRemoveSignature('investor')}
                  />
                </Grid>
                {isAdmin && (
                  <Grid item xs={12} md={6}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        mb: { xs: 0.75, md: 1.5 },
                        fontSize: { xs: '0.7rem', md: '0.9rem' }
                      }}
                    >
                      Trust Fund Manager Signature
                    </Typography>
                    <SignatureDisplay
                      type="admin"
                      signature={trustInfo.trustFundManagerSignatureBase64}
                      onEdit={() => handleOpenSignatureModal('admin')}
                      onRemove={() => handleRemoveSignature('admin')}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {isAdmin && (
            <Card
              sx={{
                mb: { xs: 1.5, md: 3 },
                borderRadius: { xs: 1, md: 2 },
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                mx: { xs: 0.5, md: 0 }
              }}
            >
              <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.5, md: 1 },
                    mb: { xs: 1, md: 2 }
                  }}
                >
                  <FaCog size={isMobile ? 14 : 20} color={theme.palette.primary.main} />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', md: '1rem' } }}
                  >
                    Operational Cost Percentages
                  </Typography>
                </Box>
                <Divider sx={{ mb: { xs: 1, md: 2 } }} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                    gap: { xs: 1, md: 2 }
                  }}
                >
                  {[
                    {
                      label: 'Buy - Income',
                      unitType: 'incomeUnits' as const,
                      perType: 'buy' as const,
                      value: values.buyUnitsIncome,
                      key: 'buyUnitsIncome',
                      color: '#4caf50'
                    },
                    {
                      label: 'Buy - Growth',
                      unitType: 'growthUnits' as const,
                      perType: 'buy' as const,
                      value: values.buyUnitsGrowth,
                      key: 'buyUnitsGrowth',
                      color: '#4caf50'
                    },
                    {
                      label: 'Sell - Income',
                      unitType: 'incomeUnits' as const,
                      perType: 'sell' as const,
                      value: values.sellUnitsIncome,
                      key: 'sellUnitsIncome',
                      color: '#2196f3'
                    },
                    {
                      label: 'Sell - Growth',
                      unitType: 'growthUnits' as const,
                      perType: 'sell' as const,
                      value: values.sellUnitsGrowth,
                      key: 'sellUnitsGrowth',
                      color: '#2196f3'
                    }
                  ].map((config) => (
                    <Card
                      key={config.key}
                      sx={{
                        borderRadius: { xs: 1, md: 2 },
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 0.75, md: 1.5 },
                          '&:last-child': { pb: { xs: 0.75, md: 1.5 } }
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            mb: { xs: 0.75, md: 1.5 },
                            fontSize: { xs: '0.65rem', md: '0.9rem' },
                            color: '#333'
                          }}
                        >
                          {config.label}
                        </Typography>
                        <TextField
                          fullWidth
                          type="number"
                          value={config.value}
                          onChange={(e) => setValues({ ...values, [config.key]: e.target.value })}
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ '& p': { fontSize: { xs: '0.65rem', md: '0.75rem' } } }}
                              >
                                %
                              </InputAdornment>
                            ),
                            sx: { fontSize: { xs: '0.65rem', md: '0.875rem' } }
                          }}
                          sx={{ mb: { xs: 0.75, md: 1.5 } }}
                        />
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          onClick={() =>
                            handleUpdate(config.unitType, config.perType, config.value)
                          }
                          disabled={loading}
                          startIcon={
                            loading ? (
                              <CircularProgress size={isMobile ? 10 : 14} />
                            ) : (
                              <FaSave size={isMobile ? 9 : 12} />
                            )
                          }
                          sx={{
                            backgroundColor: config.color,
                            textTransform: 'none',
                            fontSize: { xs: '0.6rem', md: '0.75rem' },
                            fontWeight: 600,
                            py: { xs: 0.4, md: 0.5 }
                          }}
                        >
                          Update
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: { xs: 0.5, md: 1 },
              px: { xs: 0.5, md: 0 }
            }}
          >
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'large'}
              onClick={handleSaveAll}
              disabled={saveLoading}
              startIcon={
                saveLoading ? (
                  <CircularProgress size={isMobile ? 14 : 18} />
                ) : (
                  <FaSave size={isMobile ? 12 : 16} />
                )
              }
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.7rem', md: '0.85rem' },
                fontWeight: 600,
                backgroundColor: '#667eea',
                paddingX: { xs: 2, md: 3 },
                py: { xs: 0.5, md: 1 },
                '&:hover': { backgroundColor: '#5568d3' }
              }}
            >
              {isAdmin ? 'Save All Changes' : 'Save Signature'}
            </Button>
          </Box>
        </>
      )}

      <SignatureModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSave={handleSaveSignature}
        title={`${
          currentSignatureType === 'investor' ? 'Investor' : 'Trust Fund Manager'
        } Signature`}
      />
    </Container>
  );
};

export default FundManagementConfigurationPanel;
